import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import SignatureCanvas from 'react-signature-canvas';
import { useSettings } from '../../../context/SettingContext';
import { DatalistInput } from '../components/dataListInput';
import { SignatureModal } from '../../../components/Form/SignatureModel';
import { fetchLegalAdviceById, saveLegalAdviceDraft } from '../../../services/LegalAdviceFourService';

interface LegalAdviceRowState {
  id: number;
  srNo: string;
  lawRegisterNo: string;
  dateAndSection: string;
  courtAndCaseNo: string;
  stationAndCrNo: string;
  orderDateAndSummary: string;
  advocateOpinion: string;
  officerOpinion: string;
  advocateName: string;
}

interface LegalAdviceFormFourState {
  officeId: string;
  officeName: string;
  legalNo:string;
  signatureImage: string;
  signName: string;
  signerName: string;
  sinnerOfficeName: string;
  rows: LegalAdviceRowState[];
}

const emptyRow = (id: number): LegalAdviceRowState => ({
  id,
  srNo: '',
  lawRegisterNo: '',
  dateAndSection: '',
  courtAndCaseNo: '',
  stationAndCrNo: '',
  orderDateAndSummary: '',
  advocateOpinion: '',
  officerOpinion: '',
  advocateName: '',
});

export default function LegalAdviceFourForm() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const { settings } = useSettings();
  const sigCanvasRef = useRef<SignatureCanvas | null>(null);
  const officeOptions = settings?.offices?.map((office) => office.name) || [];
  const [isLoadingRecord, setIsLoadingRecord] = useState(Boolean(id));
  const [isSaving, setIsSaving] = useState(false);
  const [isSigning, setIsSigning] = useState(false);
  const [formData, setFormData] = useState<LegalAdviceFormFourState>({
    officeId: '',
    officeName: '',
    legalNo: '',
    signatureImage: '',
    signName: '',
    signerName: '',
    sinnerOfficeName: '',
    rows: [emptyRow(1)],
  });

  useEffect(() => {
    if (!id) return;

    const loadRecord = async () => {
      try {
        setIsLoadingRecord(true);
        const data = await fetchLegalAdviceById(id);
        const officeId = data.officeId || data.originOffice?.id || '';
        const officeName = data.officeName || data.originOffice?.name || settings?.offices?.find((office) => office.id === officeId)?.name || '';
        setFormData({
          officeId,
          officeName,
          legalNo: data.legalNo || '',
          signatureImage: data.signatureImage || '',
          signName: data.signName || data.signerName || '',
          signerName: data.signerName || data.signName || '',
          sinnerOfficeName: data.sinnerOfficeName || '',
          rows: [{
            id: 1,
            srNo: data.srNo || '',
            lawRegisterNo: data.lawRegisterNo || '',
            dateAndSection: data.dateAndSection || '',
            courtAndCaseNo: data.courtAndCaseNo || '',
            stationAndCrNo: data.stationAndCrNo || '',
            orderDateAndSummary: data.orderDateAndSummary || '',
            advocateOpinion: data.advocateOpinion || '',
            officerOpinion: data.officerOpinion || '',
            advocateName: data.advocateName || '',
          }],
        });
      } catch (error) {
        console.error('Failed to load legal advice form four:', error);
        alert('အချက်အလက်များ ဆွဲယူ၍ မရရှိပါ။');
      } finally {
        setIsLoadingRecord(false);
      }
    };

    loadRecord();
  }, [id, settings?.offices]);

  const handleRowChange = (rowId: number, field: keyof LegalAdviceRowState, value: string) => {
    setFormData((current) => ({
      ...current,
      rows: current.rows.map((row) => row.id === rowId ? { ...row, [field]: value } : row),
    }));
  };

  const handleAddRow = () => {
    const nextId = Math.max(...formData.rows.map((row) => row.id), 0) + 1;
    setFormData((current) => ({ ...current, rows: [...current.rows, emptyRow(nextId)] }));
  };

  const handleRemoveRow = (rowId: number) => {
    if (formData.rows.length === 1) {
      alert('အနည်းဆုံး အချက်အလက် Row တစ်ခု ရှိနေရပါမည်။');
      return;
    }
    setFormData((current) => ({ ...current, rows: current.rows.filter((row) => row.id !== rowId) }));
  };

  const handleSaveSignature = () => {
    if (!sigCanvasRef.current || sigCanvasRef.current.isEmpty()) {
      alert('လက်မှတ် အရင်ရေးထိုးပေးပါရန်။');
      return;
    }

    try {
      const canvas = sigCanvasRef.current.getTrimmedCanvas();
      setFormData((current) => ({ ...current, signatureImage: canvas.toDataURL('image/png') }));
      setIsSigning(false);
    } catch {
      const canvas = sigCanvasRef.current.getCanvas();
      setFormData((current) => ({ ...current, signatureImage: canvas.toDataURL('image/png') }));
      setIsSigning(false);
    }
  };

  const handleClearSignature = () => {
    sigCanvasRef.current?.clear();
    setFormData((current) => ({ ...current, signatureImage: '' }));
  };

  const handleSaveForm = async () => {
    try {
      setIsSaving(true);
      const payload = formData.rows.map((row) => ({
        ...row,
        officeId: formData.officeId || undefined,
        officeName: formData.officeName || undefined,
        legalNo: formData.legalNo || undefined,
        signatureImage: formData.signatureImage || undefined,
        signName: formData.signName || undefined,
        signerName: formData.signerName || formData.signName || undefined,
        sinnerOfficeName: formData.sinnerOfficeName || undefined,
        id: id || undefined,
        status: 'DRAFT' as const,
      }));
      await saveLegalAdviceDraft(id || null, payload as unknown as Parameters<typeof saveLegalAdviceDraft>[1]);
      await queryClient.invalidateQueries({ queryKey: ['legalFormFour'] });
      alert(id ? 'အချက်အလက်များ ပြင်ဆင်သိမ်းဆည်းပြီးပါပြီ။' : 'အချက်အလက်များကို အောင်မြင်စွာ သိမ်းဆည်းပြီးပါပြီ။');
      navigate(-1);
    } catch (error: any) {
      console.error('Failed to save legal advice form four:', error);
      alert(error?.message || 'ဒေတာများ သိမ်းဆည်းရာတွင် အမှားအယွင်းရှိနေပါသည်။');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoadingRecord) {
    return <div className="min-h-screen bg-slate-100 flex items-center justify-center font-sans text-sm text-slate-600">အချက်အလက်များ ပြန်လည်ဆွဲယူနေပါသည်...</div>;
  }

  const fields: Array<{ key: Exclude<keyof LegalAdviceRowState, 'id'>; multiline?: boolean }> = [
    { key: 'srNo' }, { key: 'lawRegisterNo', multiline: true }, { key: 'dateAndSection', multiline: true },
    { key: 'courtAndCaseNo' }, { key: 'stationAndCrNo', multiline: true }, { key: 'orderDateAndSummary', multiline: true },
    { key: 'advocateOpinion', multiline: true }, { key: 'officerOpinion', multiline: true }, { key: 'advocateName' },
  ];

  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4 flex flex-col items-center justify-start font-serif text-black">
      <div className="w-full max-w-[1400px] mb-4 flex justify-between items-center font-sans">
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-xs font-semibold px-3 py-2 rounded shadow-sm transition">← နောက်သို့ (Back)</button>
          <h2 className="text-lg font-bold text-slate-800">{id ? 'ဥပရအမှုလိုက်ပုံစံ ပြင်ဆင်ရန် (Legal Advice Form 4)' : 'ဥပရအမှုလိုက်ပုံစံ (၄)'}</h2>
        </div>
        <button type="button" onClick={() => alert('ပရင့်ထုတ်ရန် အစမ်းကြည့်ရှုခြင်း...')} className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium px-4 py-2 rounded shadow transition">📄 Print Preview</button>
      </div>

      <div className="w-full max-w-[1400px] bg-white shadow-xl border border-gray-300 p-8 space-y-6 overflow-x-auto">
        <div className="text-right text-xs font-bold">ဥပရအမှုလိုက်ပုံစံ (၄)</div>
        <div className="text-center space-y-1">
          <h1 className="text-base font-bold">ပြည်ထောင်စုသမ္မတမြန်မာနိုင်ငံတော်အစိုးရ</h1>
          <h2 className="text-sm font-bold">ပြည်ထောင်စုရှေ့နေချုပ်ရုံး</h2>
          <div className="flex justify-center items-center gap-2 mt-1">
            <DatalistInput
              name="officeName"
              value={formData.officeName}
              onChange={(event) => {
                const office = settings?.offices?.find((item) => item.name === event.target.value);
                setFormData((current) => ({ ...current, officeName: event.target.value, officeId: office?.id || '' }));
              }}
              placeholder="ရုံး"
              options={officeOptions}
              className="w-64 text-center bg-transparent border-b border-black focus:outline-none py-0.5"
            />
            <span className="font-bold">ရုံး</span>
          </div>
          <div className="text-sm font-bold pt-3 underline underline-offset-4">သမ္မာန်မှု၊ အကျဉ်းနည်းဖြင့်စစ်ဆေးသော အမှုပြီးပြတ်ချက်ပုံစံ</div>
        </div>

        <div className="w-full overflow-x-auto border border-black">
          <table className="w-full table-fixed text-center border-collapse text-[11px] min-w-[1528px]">
            <colgroup>
              <col className="w-14" />
              <col className="w-36" />
              <col className="w-40" />
              <col className="w-36" />
              <col className="w-40" />
              <col className="w-48" />
              <col className="w-56" />
              <col className="w-56" />
              <col className="w-40" />
              <col className="w-16" />
            </colgroup>
            <thead>
              <tr className="bg-slate-50 border-b border-black font-bold align-middle">
                <th rowSpan={2} className="border-r border-black p-2 min-w-[110px]">အမှတ် စဉ်</th>
                <th rowSpan={2} className="border-r border-black p-2 min-w-[110px]">
                  ဥပရ မှတ်ပုံတင် (
                  <input
                    type="text"
                    name="legalNo"
                    aria-label="ဥပရ မှတ်ပုံတင် အမှတ်"
                    value={formData.legalNo}
                    onChange={(event) => setFormData((current) => ({ ...current, legalNo: event.target.value }))}
                    className="w-10 border-b border-black bg-transparent text-center focus:outline-none"
                  />
                  ) အမှတ်စဉ်
                </th>
                <th rowSpan={2} className="border-r border-black p-2 min-w-[110px]">စွဲတင်သည့် ရက်စွဲနှင့် ဥပဒေပုဒ်မ</th>
                <th rowSpan={2} className="border-r border-black p-2 min-w-[110px]">တရားရုံး အမှုအမှတ်</th>
                <th rowSpan={2} className="border-r border-black p-2 min-w-[110px]">ရဲစခန်းအမည်နှင့် (ပ) အမှတ်</th>
                <th rowSpan={2} className="border-r border-black p-2 min-w-[110px]">အမိန့်ချသည့် ရက်စွဲနှင့် အမိန့်အကျဉ်း</th>
                <th colSpan={2} className="border-r border-black p-2 min-w-[220px]">အယူခံမှု၊ပြင်ဆင်မှု အထွေထွေမှုတက်ရန် ရှိ/မရှိ မှတ်ချက်</th>
                <th rowSpan={2} className="border-r border-black p-2 min-w-[110px]">အမှုလိုက် ဥပဒေအရာရှိ အမည်</th>
                <th rowSpan={2} className="p-2 w-14 font-sans text-gray-500">Action</th>
              </tr>
              <tr className="bg-slate-50 border-b border-black font-bold align-middle">
                <th className="border-r border-black p-2 min-w-[110px]">အမှုလိုက် ဥပဒေအရာရှိ</th>
                <th className="border-r border-black p-2 min-w-[110px]">တာဝန်ခံ ဥပဒေအရာရှိ</th>
              </tr>
              <tr className="h-7 bg-slate-50 border-b border-black text-[10px] font-semibold text-slate-600">
                {['(၁)', '(၂)', '(၃)', '(၄)', '(၅)', '(၆)', '(၇)', '(၈)', '(၉)'].map((number) => (
                  <th key={number} className="border-r border-black px-2 py-1">{number}</th>
                ))}
                <th className="p-1" aria-hidden="true" />
              </tr>
            </thead>
            <tbody>
              {formData.rows.map((row) => (
                <tr key={row.id} className="border-b border-black align-top">
                  {fields.map(({ key, multiline }) => (
                    <td key={key} className="border-r border-black p-1.5">
                      {multiline ? (
                        <textarea
                          rows={3}
                          aria-label={key}
                          value={row[key]}
                          onChange={(event) => handleRowChange(row.id, key, event.target.value)}
                          className="min-h-20 w-full resize-none rounded-sm border border-transparent bg-transparent px-1.5 py-1.5 text-left leading-relaxed align-top whitespace-pre-wrap focus:border-blue-400 focus:bg-blue-50/30 focus:outline-none"
                        />
                      ) : (
                        <input
                          type="text"
                          aria-label={key}
                          value={row[key]}
                          onChange={(event) => handleRowChange(row.id, key, event.target.value)}
                          className="min-h-10 w-full rounded-sm border border-transparent bg-transparent px-1.5 py-1.5 text-left leading-relaxed focus:border-blue-400 focus:bg-blue-50/30 focus:outline-none"
                        />
                      )}
                    </td>
                  ))}
                  <td className="p-1 align-middle border-l border-black font-sans"><button type="button" onClick={() => handleRemoveRow(row.id)} disabled={formData.rows.length === 1} className="rounded p-1 text-red-500 hover:bg-red-50 hover:text-red-700 disabled:opacity-30 text-xs font-bold">❌</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {!id && <button type="button" onClick={handleAddRow} className="bg-slate-800 hover:bg-slate-950 text-white text-xs font-medium px-4 py-2 rounded shadow transition flex items-center gap-1 font-sans">➕ အတန်းအသစ်ထည့်ရန် (Add New Row)</button>}

        <div className="mt-12 pr-6 flex justify-end font-sans">
          <div className="w-72 space-y-3">
            <div className="flex items-center">
              <span className="w-24 text-right text-sm whitespace-nowrap mr-2">လက်မှတ်</span>
              <button type="button" onClick={() => setIsSigning(true)} className="flex-1 min-h-[36px] border-b border-dotted border-black flex items-center justify-center hover:bg-gray-50">
                {formData.signatureImage ? <img src={formData.signatureImage} alt="Signature" className="h-8 object-contain" /> : <span className="text-gray-400 text-xs">✍️ လက်မှတ်ထိုးရန် နှိပ်ပါ</span>}
              </button>
              {formData.signatureImage && <button type="button" onClick={handleClearSignature} className="ml-2 text-xs text-rose-600" title="လက်မှတ်ရှင်းလင်းမည်">✕</button>}
            </div>
            <div className="flex items-center">
              <span className="w-24 text-right text-sm whitespace-nowrap mr-2">အမည်</span>
              <input type="text" value={formData.signName} onChange={(event) => setFormData((current) => ({ ...current, signName: event.target.value, signerName: event.target.value }))} className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none text-center" />
            </div>
            <div className="text-center text-xs font-bold">တာဝန်ခံဥပဒေအရာရှိ</div>
            <div className="flex items-center">
              <DatalistInput name="sinnerOfficeName" value={formData.sinnerOfficeName} onChange={(event) => setFormData((current) => ({ ...current, sinnerOfficeName: event.target.value }))} options={officeOptions} className="flex-1 text-center bg-transparent border-b border-dotted border-black focus:outline-none" />
              <span className="w-24 text-left text-sm ml-2 whitespace-nowrap">ဥပဒေရုံး</span>
            </div>
          </div>
        </div>
        <div className="text-left text-[11px] font-bold text-gray-600 pt-4">* PTI အမှုမှတ်တမ်း</div>
      </div>

      <div className="w-full max-w-[1400px] mt-4 flex justify-end font-sans">
        <button type="button" onClick={handleSaveForm} disabled={isSaving} className={`bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold px-6 py-2.5 rounded shadow-md transition flex items-center gap-2 ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}>{isSaving ? 'သိမ်းဆည်းနေပါသည်...' : id ? '💾 ပြင်ဆင်ချက်များ သိမ်းဆည်းမည်' : '💾 ဒေတာများအားလုံးသိမ်းဆည်းမည်'}</button>
      </div>
      <SignatureModal isOpen={isSigning} sigCanvasRef={sigCanvasRef} onClose={() => setIsSigning(false)} onClear={() => sigCanvasRef.current?.clear()} onSave={handleSaveSignature} />
    </div>
  );
}
